// Unclosed link — `[text` without a following `](` on the same line.
const UNCLOSED_LINK_REGEX = /\[[^\]]*$/u;
// Strip leading list-item marker: unordered (* / - / +) or ordered (N.) followed by at least one space.
const LIST_PREFIX_REGEX = /^(\s*(?:[*+\-]|\d+\.)\s+)/u;
// Fence delimiter lines.
const FENCE_DELIMITER_REGEX = /^```/u;
// Thematic break lines (e.g. `***`, `* * *`, `---`, `___`) are block-level syntax.
// Spec: three or more `*`, `-`, or `_`, optionally separated by spaces, with nothing else on the line.
const THEMATIC_BREAK_REGEX = /^\s{0,3}((\*[ \t]*){3,}|([ \t]*-){3,}|(_[ \t]*){3,})\s*$/u;

/**
 * Returns true if the given pre-processed line (escape sequences and balanced
 * inline code spans already stripped) has unbalanced markdown inline syntax —
 * e.g. the user has typed `*ital` but not yet the closing `*`.
 *
 * Tokens checked (all require balanced pairs on the same line):
 *   - `` ` `` (inline code) — odd count of backticks
 *   - `*` / `**` / `***` (em / strong / bold-italic) — odd count per run-length
 *   - `[` without a matching `](` later on the line (link syntax)
 */
function lineHasUnbalancedInlineSyntax(strippedLine: string): boolean {
	// Odd backtick count → unclosed inline code span.
	const backtickCount = (strippedLine.match(/`/gu) ?? []).length;
	if (backtickCount % 2 !== 0) {
		return true;
	}

	// Each distinct asterisk run-length must appear an even number of times.
	// e.g. `**bol` has one `**` token → odd → unbalanced.
	const asteriskRuns = strippedLine.match(/\*+/gu) ?? [];
	const runCounts = new Map<number, number>();
	for (const run of asteriskRuns) {
		const len = run.length;
		runCounts.set(len, (runCounts.get(len) ?? 0) + 1);
	}
	for (const count of runCounts.values()) {
		if (count % 2 !== 0) {
			return true;
		}
	}

	// Unclosed link — `[text` without a following `](` on the same line.
	if (UNCLOSED_LINK_REGEX.test(strippedLine)) {
		return true;
	}

	return false;
}

/**
 * Pre-processes a line for syntax balance checking: strips escape sequences,
 * balanced inline code spans, and leading list-item markers so they don't
 * skew token counts.
 *
 * List-item prefixes (`* `, `- `, `+ `, or `1. ` / `123. `) begin with a
 * `*` or `-` / `+` that is structural (a block-level marker), not inline
 * emphasis syntax. Stripping them before the asterisk-balance check prevents
 * false positives on every bullet-list line.
 */
function stripLineForBalanceCheck(line: string): string {
	// Strip leading list-item marker: unordered (* / - / +) or ordered (N.)
	// followed by at least one space.
	// eslint-disable-next-line @atlassian/perf-linting/no-expensive-split-replace -- one replace per line, unavoidable
	const withoutListPrefix = line.replace(LIST_PREFIX_REGEX, '');
	// eslint-disable-next-line @atlassian/perf-linting/no-expensive-split-replace -- one replace per line, unavoidable
	return withoutListPrefix.replace(/\\.|`[^`]*`/gu, '');
}

/**
 * Returns true if the line at `cursorPos` in `markdown` has unbalanced inline
 * markdown syntax (e.g. `*ital` mid-word), OR if the document as a whole has
 * an unclosed fenced code block (triple-backtick fence without a closing pair).
 *
 * This is the primary check used by the debounce logic in MarkdownSourceView:
 * only the current line is checked for inline syntax, so a bare `*` on a
 * different line (e.g. a bullet list item) does not delay the writeback.
 * Fenced code blocks span multiple lines so they are always checked globally.
 *
 * @param markdown - The full markdown string from the CM6 buffer.
 * @param cursorPos - The character offset of the cursor in `markdown` (CM6
 *   `state.selection.main.head`). Used to identify which line is "current".
 */
export function currentLineHasUnbalancedSyntax(markdown: string, cursorPos: number): boolean {
	// Strip escape sequences first so `\`` doesn't interfere with fence detection.
	const unescaped = markdown.replace(/\\./gu, '');

	// --- Global check: unclosed fenced code block ---
	// Strip complete balanced fenced blocks (``` ... ```) so their content is
	// not counted. An odd number of remaining ``` markers means there is an
	// unclosed fence somewhere in the document.
	const withoutFences = unescaped.replace(/^```.*?^```/gmsu, '');
	const fenceCount = (withoutFences.match(/^```/gmu) ?? []).length;
	if (fenceCount % 2 !== 0) {
		return true;
	}

	// --- Per-line check: inline syntax on the cursor's line only ---
	// Find the line boundaries from the raw markdown (before escape-stripping)
	// so that cursorPos maps correctly to character offsets.
	const lineStart = markdown.lastIndexOf('\n', cursorPos - 1) + 1;
	const lineEndRaw = markdown.indexOf('\n', cursorPos);
	const lineEnd = lineEndRaw === -1 ? markdown.length : lineEndRaw;
	const currentLine = markdown.slice(lineStart, lineEnd);

	// Fence delimiter lines (``` ...) are not inline syntax — skip the check.
	if (FENCE_DELIMITER_REGEX.test(currentLine)) {
		return false;
	}

	// Thematic break lines (e.g. `***`, `* * *`, `---`, `___`) are block-level
	// syntax, not inline emphasis. A `***` line would otherwise be counted as
	// three unbalanced asterisks of length 1. Detect and skip.
	// Spec: three or more `*`, `-`, or `_`, optionally separated by spaces,
	// with nothing else on the line (except leading/trailing spaces).
	if (THEMATIC_BREAK_REGEX.test(currentLine)) {
		return false;
	}

	// If the cursor is inside a balanced fence block, the line is code content
	// not markdown syntax — skip the inline check.
	// We detect this by checking whether cursorPos falls within a stripped region.
	// Simple heuristic: count ``` markers before the cursor line in the unescaped
	// string; if the count is odd, we're inside a fence.
	const beforeCursor = unescaped.slice(0, lineStart);
	const fencesBeforeCursor = (beforeCursor.match(/^```/gmu) ?? []).length;
	if (fencesBeforeCursor % 2 !== 0) {
		return false;
	}

	return lineHasUnbalancedInlineSyntax(stripLineForBalanceCheck(currentLine));
}
